const SENSITIVE_KEY_PATTERN = /(password|secret|authorization|api[_-]?key)/i;

const SENSITIVE_QUERY_PARAMS = new Set([
  "reset_password_token",
  "change_password_token",
]);

const REDACTED = "[REDACTED]";

function sanitizeUrlLikeString(value: string): string {
  try {
    const url = new URL(value);
    for (const key of Array.from(url.searchParams.keys())) {
      if (SENSITIVE_QUERY_PARAMS.has(key)) {
        url.searchParams.set(key, REDACTED);
      }
    }
    return url.toString();
  } catch {
    if (!value.includes("?")) {
      return value;
    }

    const [base, queryString] = value.split("?");
    if (!queryString) {
      return value;
    }

    const params = new URLSearchParams(queryString);
    for (const key of Array.from(params.keys())) {
      if (new Set([...SENSITIVE_QUERY_PARAMS, "token"]).has(key)) {
        params.set(key, REDACTED);
      }
    }

    const next = params.toString();
    return next ? `${base}?${next}` : base;
  }
}

function sanitizeUnknown(value: unknown): unknown {
  if (value == null) {
    return value;
  }

  if (Array.isArray(value)) {
    return value.map(item => sanitizeUnknown(item));
  }

  if (typeof value === "string") {
    return sanitizeUrlLikeString(value);
  }

  if (typeof value !== "object") {
    return value;
  }

  const record = value as Record<string, unknown>;
  const result: Record<string, unknown> = {};

  for (const [key, entry] of Object.entries(record)) {
    if (SENSITIVE_KEY_PATTERN.test(key)) {
      result[key] = REDACTED;
      continue;
    }

    result[key] = sanitizeUnknown(entry);
  }

  return result;
}

export function sanitizeEventProps<T extends Record<string, unknown>>(
  props: T,
): T {
  return sanitizeUnknown(props) as T;
}

export function sanitizeErrorReason(reason: unknown): string {
  if (typeof reason === "string") {
    return reason.slice(0, 300);
  }

  if (reason instanceof Error) {
    return sanitizeUrlLikeString(reason.message).slice(0, 300);
  }

  return "Unknown error";
}
